import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Keyboard, Zoom, EffectFade, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/keyboard';
import 'swiper/css/zoom';
import 'swiper/css/effect-fade';
import 'swiper/css/autoplay';
import { MdFullscreen, MdFullscreenExit } from 'react-icons/md';
import { getFileType, shouldContinueVideoPlayback, shouldStartVideoPlayback } from './utils/photoHelpers';
import { useVideoManager } from './hooks/useVideoManager';
import { useMediaViewUrls } from './hooks/useMediaViewUrls';
import VideoPlayer from './VideoPlayer';

// Constants
const DEFAULT_AUTOPLAY_DELAY = 3000;
const VIDEO_AUTOPLAY_DELAY = 500;
const AUTOPLAY_RESTART_DELAY = 100;
const VIDEO_FALLBACK_DELAY = 1000;
const SWIPER_SPEED = 600;
const SWIPER_RESISTANCE_RATIO = 0.85;
const VIDEO_PREVIEW_MODE = true; // Auto-play videos briefly in slideshow

// CSS Classes
const SWIPER_BUTTON_CLASSES = '!text-white transition-opacity duration-500 !w-12 !h-12 !bg-black-50 hover:!bg-black-70 !backdrop-blur-md';
const CONTROLS_CONTAINER_CLASSES = 'absolute bottom-6 left-1/2 transform -translate-x-1/2 z-20 transition-opacity duration-500';
const CONTROL_BUTTON_CLASSES = 'p-1.5 sm:p-2 rounded-full bg-primary-20 hover:bg-primary-40 transition-colors';
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'video[controls]:not([tabindex="-1"])',
  '[tabindex]:not([tabindex="-1"])'
].join(',');

const getFocusableElements = (dialog) => {
  if (!dialog) return [];
  return Array.from(dialog.querySelectorAll(FOCUSABLE_SELECTOR))
    .filter(element => !element.closest('[aria-hidden="true"], .video-player-slideshow'));
};

// Autoplay timing options
const AUTOPLAY_OPTIONS = [
  { value: 2000, label: '2s' },
  { value: 3000, label: '3s' },
  { value: 5000, label: '5s' },
  { value: 8000, label: '8s' },
  { value: 12000, label: '12s' },
  { value: 0, label: 'Manual' }
];

/**
 * SlideshowModal component using Swiper for slideshow experience
 * Features auto-advancing slideshow with customizable autoplay timing, auto-hiding controls,
 * and intelligent video handling for wedding slideshows
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Array} props.photos - Array of photo filenames
 * @param {number} props.startIndex - Index to start the slideshow at
 * @param {function} props.onClose - Handler for closing the modal
 * @param {number} props.autoHideDelay - Delay in ms before controls auto-hide (default: 3000)
 * @returns {JSX.Element|null}
 */
const SlideshowModal = ({
  isOpen,
  photos,
  startIndex = 0,
  onClose,
  autoHideDelay = 3000,
  hasNextPage,
  fetchNextPage,
  galleryId
}) => {
  const swiperRef = useRef(null);
  const videoStartTimeoutRef = useRef(null);
  const videoTimeoutRef = useRef(null);
  const videoPlaybackGenerationRef = useRef(0);
  const isPlayingRef = useRef(true);
  const controlsTimeoutRef = useRef(null);
  const controlsRef = useRef(null);
  const modalRef = useRef(null);
  const closeButtonRef = useRef(null);
  const previousFocusRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(true);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [autoplayEnabled, setAutoplayEnabled] = useState(false);
  const [videoReadyTick, setVideoReadyTick] = useState(0);
  const [skipVideos, setSkipVideos] = useState(true);
  const [videoOnlyFallback, setVideoOnlyFallback] = useState(false);
  const [shuffleMode, setShuffleMode] = useState(false);
  const [repeatMode, setRepeatMode] = useState(true);
  const [isPrefetching, setIsPrefetching] = useState(
    () => Boolean(isOpen && hasNextPage && fetchNextPage && !photos?.length)
  );
  isPlayingRef.current = isPlaying;

  const [currentAutoHideDelay] = useState(autoHideDelay);
  const [autoplayDelay, setAutoplayDelay] = useState(DEFAULT_AUTOPLAY_DELAY);

  // State to track the currently active slide index for lazy loading
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);

  // Create photoAccess object for compatibility
  const photoAccess = useMemo(() => {
    if (!photos) return null;
    return {
      getAllLoadedPhotos: () => photos,
      getPhotoByIndex: (index) => photos[index],
      totalPhotos: photos.length
    };
  }, [photos]);

  const hasPhotos = useMemo(() => {
    return (photoAccess?.getAllLoadedPhotos() || [])
      .some(filename => getFileType(filename) !== 'video');
  }, [photoAccess]);
  const videosHidden = skipVideos && hasPhotos;

  useEffect(() => {
    const loadedMedia = photoAccess?.getAllLoadedPhotos() || [];
    if (isOpen && loadedMedia.length > 0 && !hasPhotos) {
      setVideoOnlyFallback(true);
      if (skipVideos) {
        setSkipVideos(false);
      }
    }
  }, [hasPhotos, isOpen, photoAccess, skipVideos]);

  // Helper function to shuffle array using Fisher-Yates algorithm
  const shuffleArray = useCallback((array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }, []);

  // Store the shuffled order to maintain consistency when new photos are added
  const [shuffledOrder, setShuffledOrder] = useState([]);
  const [lastPhotoCount, setLastPhotoCount] = useState(0);
  // Track photos that have been added to shuffle queue to avoid replaying
  const [photosInQueue, setPhotosInQueue] = useState(new Set());
  // Track the last index to detect when we've completed a loop
  const [lastIndex, setLastIndex] = useState(-1);

  // Filter and optionally shuffle photos based on settings
  const displayPhotos = useMemo(() => {
    const allPhotos = photoAccess?.getAllLoadedPhotos() || [];
    let filteredPhotos = videosHidden
      ? allPhotos.filter(filename => getFileType(filename) !== 'video')
      : allPhotos;
    
    if (!shuffleMode) {
      // Normal mode: just return filtered photos in order
      return filteredPhotos;
    }

    // SHUFFLE MODE: Progressive loading strategy
    const currentPhotoCount = filteredPhotos.length;
    
    // First time shuffling - initialize the queue
    if (shuffledOrder.length === 0) {
      const newShuffledOrder = shuffleArray(filteredPhotos);
      setShuffledOrder(newShuffledOrder);
      setLastPhotoCount(currentPhotoCount);
      setPhotosInQueue(new Set(filteredPhotos));
      return newShuffledOrder;
    }
    
    // New photos loaded - append them to the END (shuffled among themselves)
    if (currentPhotoCount > lastPhotoCount) {
      // Find photos that aren't in our queue yet
      const newPhotos = filteredPhotos.filter(photo => !photosInQueue.has(photo));
      
      if (newPhotos.length > 0) {
        // Shuffle the new photos among themselves
        const shuffledNewPhotos = shuffleArray(newPhotos);
        
        // Append to the end of existing queue
        const updatedOrder = [...shuffledOrder, ...shuffledNewPhotos];
        
        // Update tracking
        const updatedQueue = new Set(photosInQueue);
        newPhotos.forEach(photo => updatedQueue.add(photo));
        
        setShuffledOrder(updatedOrder);
        setLastPhotoCount(currentPhotoCount);
        setPhotosInQueue(updatedQueue);
        return updatedOrder;
      }
    }
    
    // Handle removed photos (cleanup)
    const validShuffledOrder = shuffledOrder.filter(photo => filteredPhotos.includes(photo));
    if (validShuffledOrder.length !== shuffledOrder.length) {
      const updatedQueue = new Set(validShuffledOrder);
      setShuffledOrder(validShuffledOrder);
      setLastPhotoCount(validShuffledOrder.length);
      setPhotosInQueue(updatedQueue);
      return validShuffledOrder;
    }
    
    return shuffledOrder;
  }, [photoAccess, videosHidden, shuffleMode, shuffleArray, shuffledOrder, lastPhotoCount, photosInQueue]);

  // Get video filenames for smart preloading (current, next, and previous for smooth playback)
  const videoFilesToFetch = useMemo(() => {
    if (!isOpen || !displayPhotos.length) return [];
    const videos = [];

    // Add current video if it's a video
    const currentFilename = displayPhotos[activeSlideIndex];
    if (getFileType(currentFilename) === 'video') {
      videos.push(currentFilename);
    }

    // Add next video if it exists and is a video (for smoother transitions)
    const nextIndex = (activeSlideIndex + 1) % displayPhotos.length;
    const nextFilename = displayPhotos[nextIndex];
    if (getFileType(nextFilename) === 'video' && !videos.includes(nextFilename)) {
      videos.push(nextFilename);
    }

    // Add previous video if it exists and is a video (for backward navigation)
    const prevIndex = activeSlideIndex === 0 ? displayPhotos.length - 1 : activeSlideIndex - 1;
    const prevFilename = displayPhotos[prevIndex];
    if (getFileType(prevFilename) === 'video' && !videos.includes(prevFilename)) {
      videos.push(prevFilename);
    }

    return videos;
  }, [isOpen, displayPhotos, activeSlideIndex]);

  // Use video manager hook with smart preloading - fetch current, next, and previous videos
  const { setVideoPlayers, getStreamData, isStreamLoading, cleanupPlayers } = useVideoManager(videoFilesToFetch, isOpen, galleryId);
  const activeStreamUrl = getStreamData(displayPhotos[activeSlideIndex])?.url || null;

  // Use signed view URLs hook (lazy full-size image loading + signed thumbnails)
  const { getViewUrl, getThumbnailUrl: getSignedThumbnailUrl } = useMediaViewUrls(displayPhotos, activeSlideIndex, isOpen, galleryId);

  // Helper function to clear video timeout
  const clearVideoTimeout = useCallback(() => {
    videoPlaybackGenerationRef.current += 1;
    if (videoStartTimeoutRef.current) {
      clearTimeout(videoStartTimeoutRef.current);
      videoStartTimeoutRef.current = null;
    }
    if (videoTimeoutRef.current) {
      clearTimeout(videoTimeoutRef.current);
      videoTimeoutRef.current = null;
    }
  }, []);

  // Helper function to stop all videos
  const stopAllVideos = useCallback(() => {
    const allVideos = document.querySelectorAll('video[data-filename]');
    allVideos.forEach(video => {
      video.pause();
      video.currentTime = 0;
    });
  }, []);

  // Helper function to restore scrollbars
  const restoreScrollbars = useCallback(() => {
    document.body.style.overflow = '';
    document.documentElement.style.overflow = '';
  }, []);

  // Auto-hide controls functionality
  const showControls = useCallback(() => {
    setControlsVisible(true);
    // Enable autoplay on first user interaction
    if (!autoplayEnabled) {
      setAutoplayEnabled(true);
    }
    // Clear existing timeout
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    // Set new timeout to hide controls (only if not set to "never")
    if (currentAutoHideDelay > 0) {
      controlsTimeoutRef.current = setTimeout(() => {
        if (controlsRef.current?.contains(document.activeElement)) return;
        setControlsVisible(false);
      }, currentAutoHideDelay);
    }
  }, [currentAutoHideDelay, autoplayEnabled]);

  // Change autoplay timing
  const changeAutoplayTiming = useCallback((newDelay) => {
    setAutoplayDelay(newDelay);
    if (swiperRef.current) {
      if (newDelay === 0) {
        // Manual mode - disable autoplay
        swiperRef.current.autoplay.stop();
        setIsPlaying(false);
        clearVideoTimeout();
      } else {
        // Update the autoplay delay dynamically
        swiperRef.current.autoplay.delay = newDelay;
        if (isPlaying) {
          // Restart autoplay with new delay
          swiperRef.current.autoplay.stop();
          swiperRef.current.autoplay.start();
        } else {
          // Start autoplay if it was stopped
          swiperRef.current.autoplay.start();
          setIsPlaying(true);
        }
      }
    }
    showControls(); // Keep controls visible when changing settings
  }, [isPlaying, showControls, clearVideoTimeout]);

  // Helper function to render photo/video content with smart lazy loading for both photos and videos
  const renderPhotoContent = useCallback((index) => {
    const filename = displayPhotos[index];
    const fileType = getFileType(filename);
    const isActiveSlide = index === activeSlideIndex;
    
    // Check if this slide is adjacent to the active slide (for preloading)
    const nextIndex = (activeSlideIndex + 1) % displayPhotos.length;
    const prevIndex = activeSlideIndex === 0 ? displayPhotos.length - 1 : activeSlideIndex - 1;
    const isAdjacentSlide = index === nextIndex || index === prevIndex;
    const shouldRenderVideo = isActiveSlide || isAdjacentSlide;

    if (fileType === 'video') {
      // Render VideoPlayer for active and adjacent slides to enable preloading
      if (!shouldRenderVideo) {
        // For distant slides, show a video thumbnail placeholder
        return (
          <div className="relative w-full h-full flex items-center justify-center bg-black">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-surface-50 text-lg">Video</div>
            </div>
            <img
              src={getSignedThumbnailUrl(filename)}
              className="w-full h-full object-contain max-h-screen opacity-50"
              alt={`Video thumbnail ${index + 1} of ${displayPhotos.length}`}
              onError={(e) => {
                // Fallback to app logo if thumbnail fails to load
                e.target.src = '/placeholder-logo.svg';
                e.target.className = 'w-full h-full object-contain bg-primary-10 opacity-50';
              }}
            />
          </div>
        );
      }

      const streamData = getStreamData(filename);
      const isLoading = isStreamLoading(filename);
      const showVideoControls = !VIDEO_PREVIEW_MODE || videoOnlyFallback || !hasPhotos;

      return (
        <div className="relative w-full h-full flex items-center justify-center">
          <VideoPlayer
            key={`${filename}-${showVideoControls ? 'full-controls' : 'preview-controls'}`}
            filename={filename}
            streamData={streamData}
            isLoading={isLoading}
            showControls={showVideoControls}
            muted={true} // Mute videos in slideshow by default
            preload="metadata" // Allow metadata loading for better mobile performance
            className={`w-full h-full object-contain max-h-screen ${
              showVideoControls ? '' : 'video-player-slideshow'
            }`}
             onEnded={() => {
              // Video ended naturally (longer than autoplayDelay)
              // Just stop and reset - the timeout will handle advancing
              const videoElement = document.querySelector(`video[data-filename="${filename}"]`);
              if (videoElement) {
                videoElement.pause();
                videoElement.currentTime = 0;
              }
             }}
             onReady={() => setVideoReadyTick((tick) => tick + 1)}
             setVideoPlayers={setVideoPlayers}
            posterUrl={getSignedThumbnailUrl(filename)}
          />
        </div>
      );
    } else {
      // Lazy load photos - only load active and adjacent slides (same logic as videos)
      const shouldLoadPhoto = isActiveSlide || isAdjacentSlide;
      
      if (!shouldLoadPhoto) {
        // For distant photo slides, show thumbnail placeholder
        return (
          <div className="relative w-full h-full flex items-center justify-center bg-gray-100">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-gray-400 text-lg">Photo</div>
            </div>
            <img
              src={getSignedThumbnailUrl(filename)}
              className="w-full h-full object-contain max-h-screen opacity-50"
              alt={`Thumbnail ${index + 1} of ${displayPhotos.length}`}
              onError={(e) => {
                // Fallback to app logo if thumbnail fails to load
                e.target.src = '/placeholder-logo.svg';
                e.target.className = 'w-full h-full object-contain bg-primary-10 opacity-50';
              }}
            />
          </div>
        );
      }

      // Load full resolution photo for active and adjacent slides
      const signedUrl = getViewUrl(filename);

      if (!signedUrl) {
        // Signed URL not ready yet — keep placeholder instead of a 403 direct URL
        return (
          <div className="relative w-full h-full flex items-center justify-center bg-gray-100">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-gray-400 text-lg">Loading photo...</div>
            </div>
          </div>
        );
      }

      return (
        <div className="relative w-full h-full flex items-center justify-center">
          <img
            src={signedUrl}
            className="w-full h-full object-contain max-h-screen"
            alt={`${index + 1} of ${displayPhotos.length}`}
            onError={(e) => {
              // Fallback to app logo if image fails to load
              e.target.src = '/placeholder-logo.svg';
              e.target.className = 'w-full h-full object-contain bg-primary-10';
            }}
          />
        </div>
      );
    }
  }, [displayPhotos, activeSlideIndex, getStreamData, isStreamLoading, setVideoPlayers, getViewUrl, getSignedThumbnailUrl, hasPhotos, videoOnlyFallback]);

  // Initialize swiper and set autoplay
  const handleSwiperInit = useCallback((swiper) => {
    swiperRef.current = swiper;
    swiper.slideToLoop(0, 0); // Always start from first slide in displayPhotos

    // Set initial active slide index for lazy loading
    setActiveSlideIndex(0);

    // Start autoplay if enabled
    if (isPlaying) {
      swiper.autoplay.start();
    }

    // Show controls initially
    showControls();

    // Initial video handling is done in handleSwiperSlideChange
  }, [isPlaying, showControls]);

  // Handle slide changes
  const handleSwiperSlideChange = useCallback((swiper) => {
    const currentIndex = swiper.realIndex; // Use realIndex for loop-enabled swiper
    const currentFilename = displayPhotos[currentIndex];

    // Update active slide index for lazy loading
    setActiveSlideIndex(currentIndex);

    // Check if we're at the end and repeat is off
    if (!repeatMode && currentIndex === displayPhotos.length - 1 && isPlaying) {
      // Stop autoplay when reaching the end
      setTimeout(() => {
        if (swiperRef.current) {
          swiperRef.current.autoplay.stop();
          setIsPlaying(false);
        }
      }, autoplayDelay);
    }

    // Detect loop back: we're at index 0 and came from a higher index (completed a loop)
    const hasLoopedBack = repeatMode && shuffleMode && currentIndex === 0 && lastIndex > currentIndex;
    
    if (hasLoopedBack) {
      // Re-shuffle everything when we complete a loop
      const allPhotos = photoAccess?.getAllLoadedPhotos() || [];
      const filteredPhotos = videosHidden
        ? allPhotos.filter(filename => getFileType(filename) !== 'video')
        : allPhotos;
      
      const newShuffledOrder = shuffleArray(filteredPhotos);
      setShuffledOrder(newShuffledOrder);
      setLastPhotoCount(filteredPhotos.length);
      setPhotosInQueue(new Set(filteredPhotos));
    }
    
    // Check if we're at start (in repeat mode) - fetch more photos
    if (repeatMode && currentIndex === 0 && hasNextPage && fetchNextPage) {
      fetchNextPage();
    }
    
    // Track current index for next iteration
    setLastIndex(currentIndex);

    // Fetch more photos when we're getting close to the end of loaded photos
    // This works for both normal and shuffle mode - new photos are handled appropriately
    const photosRemaining = displayPhotos.length - currentIndex;
    const shouldFetch = photosRemaining <= 20; // Fetch when 20 photos remaining
    
    if (shouldFetch && hasNextPage && fetchNextPage) {
      fetchNextPage();
    }

    // CRITICAL: Stop all videos immediately to prevent audio bleed
    // This must happen before any new video starts playing
    stopAllVideos();
    clearVideoTimeout();
    const playbackGeneration = videoPlaybackGenerationRef.current;

    if (currentFilename) {
      const fileType = getFileType(currentFilename);

      if (fileType === 'video' && VIDEO_PREVIEW_MODE) {
        // Handle video slide - works for both auto and manual navigation
        const videoElement = document.querySelector(`video[data-filename="${currentFilename}"]`);
        if (videoElement) {
          // ALWAYS reset video to start from beginning
          videoElement.currentTime = 0;
          
          videoStartTimeoutRef.current = setTimeout(() => {
            videoStartTimeoutRef.current = null;
            const isCurrentPlayback = () => shouldContinueVideoPlayback({
              isPlaying: isPlayingRef.current,
              playbackGeneration,
              activeGeneration: videoPlaybackGenerationRef.current,
              slideIndex: currentIndex,
              activeSlideIndex: swiperRef.current?.realIndex,
            });
            if (isCurrentPlayback()) {
              videoElement.play().then(() => {
                if (!isCurrentPlayback()) {
                  videoElement.pause();
                  videoElement.currentTime = 0;
                  return;
                }
                if (!autoplayEnabled) {
                  setAutoplayEnabled(true);
                }

                videoTimeoutRef.current = setTimeout(() => {
                  if (!isCurrentPlayback()) return;
                  videoElement.pause();
                  videoElement.currentTime = 0;
                  swiperRef.current.slideNext();
                }, autoplayDelay);
              }).catch((error) => {
                console.warn('Video autoplay failed:', error);
                if (!isCurrentPlayback()) return;
                videoTimeoutRef.current = setTimeout(() => {
                  if (isCurrentPlayback()) swiperRef.current.slideNext();
                }, VIDEO_FALLBACK_DELAY);
              });
            }
          }, VIDEO_AUTOPLAY_DELAY);
        } else {
          console.warn(`Video element not found for ${currentFilename}`);
        }
      }
    }
  }, [displayPhotos, isPlaying, autoplayEnabled, clearVideoTimeout, stopAllVideos, hasNextPage, fetchNextPage, repeatMode, autoplayDelay, shuffleMode, photoAccess, videosHidden, shuffleArray, lastIndex]);

  // A video element is intentionally withheld while its signed stream URL is
  // loading, so retry autoplay when the signed player appears for the active slide.
  useEffect(() => {
    if (!isOpen || !swiperRef.current || !isPlayingRef.current) return;
    const currentFilename = displayPhotos[activeSlideIndex];
    if (getFileType(currentFilename) !== 'video') return;
    const videoElement = document.querySelector(`video[data-filename="${currentFilename}"]`);
    const canStart = shouldStartVideoPlayback({
      isActive: true,
      isPlaying: isPlayingRef.current,
      streamUrl: activeStreamUrl,
      videoElement,
    });
    if (!canStart) {
      swiperRef.current.autoplay?.stop();
      return;
    }
    if (videoStartTimeoutRef.current || videoTimeoutRef.current) return;
    swiperRef.current.autoplay?.start();
    handleSwiperSlideChange(swiperRef.current);
  }, [activeStreamUrl, activeSlideIndex, displayPhotos, handleSwiperSlideChange, isOpen, videoReadyTick]);

  // Handle skip videos toggle with proper slide position management
  const handleSkipVideosToggle = useCallback(() => {
    if (!swiperRef.current || !photoAccess || !hasPhotos) return;

    const allPhotos = photoAccess.getAllLoadedPhotos();
    const currentIndex = swiperRef.current.realIndex;
    const currentFilename = displayPhotos[currentIndex];

    // Store autoplay state before toggling
    const wasPlaying = isPlaying;

    // Toggle skip videos
    const newSkipVideos = !videosHidden;
    setSkipVideos(newSkipVideos);

    // Find the new position of the current photo in the filtered array
    let newIndex = 0;
    if (currentFilename) {
      let newDisplayPhotos = newSkipVideos
        ? allPhotos.filter(filename => getFileType(filename) !== 'video')
        : allPhotos;
      
      // Apply shuffle if enabled
      if (shuffleMode) {
        newDisplayPhotos = shuffleArray(newDisplayPhotos);
      }

      newIndex = newDisplayPhotos.findIndex(filename => filename === currentFilename);

      // If the current photo is not in the new filtered list, stay at the same relative position
      if (newIndex === -1) {
        newIndex = Math.min(currentIndex, newDisplayPhotos.length - 1);
      }
    }

    // Immediately navigate to the correct slide to avoid race conditions
    swiperRef.current.slideToLoop(newIndex, 0);

    // Ensure autoplay continues if it was playing before
    if (wasPlaying && swiperRef.current) {
      // Small delay to ensure slide transition is complete, then restart autoplay
      setTimeout(() => {
        if (swiperRef.current) {
          swiperRef.current.autoplay.start();
          // Ensure our state stays in sync
          if (!isPlaying) {
            setIsPlaying(true);
          }
          if (!autoplayEnabled) {
            setAutoplayEnabled(true);
          }
        }
      }, AUTOPLAY_RESTART_DELAY);
    }
  }, [videosHidden, displayPhotos, photoAccess, hasPhotos, isPlaying, autoplayEnabled, shuffleMode, shuffleArray]);

  // Handle shuffle toggle with proper slide position management
  const handleShuffleToggle = useCallback(() => {
    if (!swiperRef.current || !photoAccess) return;

    const currentIndex = swiperRef.current.realIndex;
    const currentFilename = displayPhotos[currentIndex];

    // Store autoplay state before toggling
    const wasPlaying = isPlaying;

    // Toggle shuffle mode
    const newShuffleMode = !shuffleMode;
    setShuffleMode(newShuffleMode);

    if (newShuffleMode) {
      // Turning shuffle ON - create new shuffle order and reset queue
      const allPhotos = photoAccess.getAllLoadedPhotos();
      const filteredPhotos = videosHidden
        ? allPhotos.filter(filename => getFileType(filename) !== 'video')
        : allPhotos;
      
      const newShuffledOrder = shuffleArray(filteredPhotos);
      setShuffledOrder(newShuffledOrder);
      setLastPhotoCount(filteredPhotos.length);
      setPhotosInQueue(new Set(filteredPhotos));
    } else {
      // Turning shuffle OFF - clear shuffle state
      setShuffledOrder([]);
      setLastPhotoCount(0);
      setPhotosInQueue(new Set());
    }

    // Find the new position of the current photo
    setTimeout(() => {
      if (swiperRef.current && currentFilename) {
        // displayPhotos will be recalculated due to the useMemo dependency
        // We need to wait for the next render cycle
        setTimeout(() => {
          const newIndex = displayPhotos.findIndex(filename => filename === currentFilename);
          const targetIndex = newIndex !== -1 ? newIndex : 0;

          // Navigate to the correct slide
          swiperRef.current.slideToLoop(targetIndex, 0);

          // Ensure autoplay continues if it was playing before
          if (wasPlaying) {
            setTimeout(() => {
              if (swiperRef.current) {
                swiperRef.current.autoplay.start();
                if (!isPlaying) {
                  setIsPlaying(true);
                }
                if (!autoplayEnabled) {
                  setAutoplayEnabled(true);
                }
              }
            }, AUTOPLAY_RESTART_DELAY);
          }
        }, 100); // Wait for displayPhotos to update
      }
    }, 50); // Small delay to allow state update
  }, [shuffleMode, displayPhotos, photoAccess, isPlaying, autoplayEnabled, videosHidden, shuffleArray]);

  // Handle repeat toggle
  const handleRepeatToggle = useCallback(() => {
    const newRepeatMode = !repeatMode;
    setRepeatMode(newRepeatMode);
    
    // If turning repeat on and we're at the end, restart autoplay
    if (newRepeatMode && swiperRef.current && !isPlaying) {
      const currentIndex = swiperRef.current.realIndex;
      if (currentIndex === displayPhotos.length - 1) {
        swiperRef.current.autoplay.start();
        setIsPlaying(true);
      }
    }
    
    showControls();
  }, [repeatMode, isPlaying, displayPhotos.length, showControls]);

  // Helper function for slide navigation
  const navigateSlide = useCallback((direction) => {
    if (swiperRef.current) {
      // Stop all videos before navigating
      stopAllVideos();
      clearVideoTimeout();
      
      if (direction === 'prev') {
        swiperRef.current.slidePrev();
      } else {
        swiperRef.current.slideNext();
      }
      // handleSwiperSlideChange will be triggered automatically and handle video playback
    }
    showControls();
  }, [showControls, stopAllVideos, clearVideoTimeout]);

  // Go to previous slide
  const goToPrev = useCallback(() => navigateSlide('prev'), [navigateSlide]);

  // Go to next slide
  const goToNext = useCallback(() => navigateSlide('next'), [navigateSlide]);

  // Toggle play/pause
  const togglePlayPause = useCallback(() => {
    if (swiperRef.current) {
      if (isPlaying) {
        swiperRef.current.autoplay.stop();
        clearVideoTimeout();
      } else {
        swiperRef.current.autoplay.start();
      }
      setIsPlaying(!isPlaying);
    }
    showControls();
  }, [isPlaying, showControls, clearVideoTimeout]);

  // Check if fullscreen is supported (many mobile browsers don't support it)
  const isFullscreenSupported = useMemo(() => {
    return !!(
      document.fullscreenEnabled ||
      document.webkitFullscreenEnabled ||
      document.mozFullScreenEnabled ||
      document.msFullscreenEnabled
    );
  }, []);

  // Toggle fullscreen
  const toggleFullscreen = useCallback(() => {
    if (!isFullscreenSupported) {
      // On mobile browsers without fullscreen support, show a helpful message
      alert('Fullscreen mode is not supported on this device. The slideshow is already optimized for your screen size!');
      return;
    }

    if (!document.fullscreenElement) {
      // Enter fullscreen
      const element = document.documentElement;
      const requestFullscreen = element.requestFullscreen || 
                               element.webkitRequestFullscreen || 
                               element.mozRequestFullScreen || 
                               element.msRequestFullscreen;
      
      if (requestFullscreen) {
        requestFullscreen.call(element).catch((error) => {
          console.warn('Failed to enter fullscreen:', error);
        });
      }
    } else {
      // Exit fullscreen
      const exitFullscreen = document.exitFullscreen || 
                             document.webkitExitFullscreen || 
                             document.mozCancelFullScreen || 
                             document.msExitFullscreen;
      
      if (exitFullscreen) {
        exitFullscreen.call(document).catch((error) => {
          console.warn('Failed to exit fullscreen:', error);
        });
      }
    }
    showControls();
  }, [showControls, isFullscreenSupported]);

  // Handle close with fullscreen exit if needed
  const handleClose = useCallback(() => {
    if (document.fullscreenElement) {
      // Exit fullscreen first, then close
      // handleFullscreenChange will handle scrollbar restoration
      document.exitFullscreen().then(() => {
        onClose();
      }).catch((error) => {
        console.warn('Failed to exit fullscreen on close:', error);
        // Still close the modal even if fullscreen exit fails
        onClose();
      });
    } else {
      // Not in fullscreen, just close
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleDialogKeyDown = (event) => {
      if (event.key === 'Escape') {
        handleClose();
        return;
      }

      if (event.key !== 'Tab' || !modalRef.current) return;

      const focusableElements = getFocusableElements(modalRef.current);
      if (!focusableElements.length) {
        event.preventDefault();
        modalRef.current.focus();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      if (document.activeElement === modalRef.current) {
        event.preventDefault();
        (event.shiftKey ? lastElement : firstElement).focus();
      } else if (!modalRef.current.contains(document.activeElement)) {
        event.preventDefault();
        (event.shiftKey ? lastElement : firstElement).focus();
      } else if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener('keydown', handleDialogKeyDown);
    return () => document.removeEventListener('keydown', handleDialogKeyDown);
  }, [handleClose, isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;

    previousFocusRef.current = document.activeElement;
    return () => {
      previousFocusRef.current?.focus?.();
      previousFocusRef.current = null;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || modalRef.current?.contains(document.activeElement)) return;
    if (displayPhotos.length) {
      modalRef.current?.focus();
    } else {
      closeButtonRef.current?.focus();
    }
  }, [displayPhotos.length, isOpen]);



  // Prefetch one page when slideshow opens to have initial buffer
  useEffect(() => {
    if (!isOpen || !hasNextPage || !fetchNextPage) return;
    let isActive = true;
    setIsPrefetching(true);
    
    const prefetchPages = async () => {
      try {
        await fetchNextPage();
      } catch (error) {
        console.warn('Error prefetching photos for slideshow:', error);
      } finally {
        if (isActive) setIsPrefetching(false);
      }
    };
    
    prefetchPages();
    return () => {
      isActive = false;
    };
  }, [isOpen, fetchNextPage, hasNextPage]); // Only run once when slideshow opens

  // Cleanup Plyr players and videos on unmount
  useEffect(() => {
    return () => {
      cleanupPlayers();

      // Stop all videos when component unmounts
      stopAllVideos();

      // Restore scrollbars when component unmounts
      restoreScrollbars();
    };
  }, [cleanupPlayers, restoreScrollbars, stopAllVideos]);

  // Set up event listeners for user activity
  useEffect(() => {
    if (!isOpen) return;

    const handleActivity = () => {
      showControls();
    };

    const handleFullscreenChange = () => {
      const isNowFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      );
      setIsFullscreen(isNowFullscreen);

      // Manage scrollbars based on fullscreen state
      if (isNowFullscreen) {
        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';
      } else {
        restoreScrollbars();
      }
    };

    // Add event listeners for mouse and touch activity
    const events = ['mousedown', 'mousemove', 'touchstart', 'touchmove', 'keydown', 'scroll', 'wheel'];
    events.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    // Add fullscreen change listeners for different browsers
    const fullscreenEvents = ['fullscreenchange', 'webkitfullscreenchange', 'mozfullscreenchange', 'msfullscreenchange'];
    fullscreenEvents.forEach(event => {
      document.addEventListener(event, handleFullscreenChange);
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      fullscreenEvents.forEach(event => {
        document.removeEventListener(event, handleFullscreenChange);
      });
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
      clearVideoTimeout();
      // Restore scrollbars when modal closes
      restoreScrollbars();
    };
  }, [isOpen, showControls, clearVideoTimeout, restoreScrollbars]);

  if (!isOpen) return null;

  if (!displayPhotos.length) {
    return (
      <div
        ref={modalRef}
        className="fixed inset-0 z-50 bg-black slideshow-modal flex items-center justify-center p-6"
        onClick={handleClose}
        role="dialog"
        aria-modal="true"
        aria-labelledby="slideshow-empty-title"
        tabIndex={-1}
      >
        <div
          className="w-full max-w-md rounded-2xl border border-surface-20 bg-black/80 p-8 text-center text-surface shadow-lg"
          onClick={(event) => event.stopPropagation()}
        >
          <h2 id="slideshow-empty-title" className="text-2xl font-semibold">
            {isPrefetching ? 'Loading slideshow…' : 'No photos or videos yet'}
          </h2>
          <p className="mt-3 text-sm text-surface-70">
            {isPrefetching
              ? 'Checking for more shared moments.'
              : 'New moments will appear here as soon as they are shared.'}
          </p>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={handleClose}
            className="mt-6 rounded-full bg-primary px-5 py-2 text-sm font-medium text-surface transition-colors hover:bg-primary-90"
            aria-label="Close slideshow"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={modalRef}
      className="fixed inset-0 z-50 bg-black slideshow-modal"
      onClick={handleClose}
      role="dialog"
      aria-modal="true"
      aria-label="Slideshow"
      tabIndex={-1}
    >
      <div className="relative w-full h-full" onClick={(e) => e.stopPropagation()}>
        {/* Swiper Slideshow */}
        <Swiper
          onSwiper={handleSwiperInit}
          onSlideChange={handleSwiperSlideChange}
          initialSlide={startIndex}
          modules={[Navigation, Keyboard, Zoom, EffectFade, Autoplay]}
          navigation={{
            nextEl: '.slideshow-button-next',
            prevEl: '.slideshow-button-prev',
          }}
          keyboard={{
            enabled: true,
            onlyInViewport: true,
          }}
          zoom={{
            maxRatio: 3,
            minRatio: 0.5,
          }}
          autoplay={{
            delay: autoplayDelay,
            disableOnInteraction: false,
            pauseOnMouseEnter: false,
          }}
          spaceBetween={0}
          slidesPerView={1}
          centeredSlides={true}
          loop={repeatMode}
          speed={SWIPER_SPEED}
          allowTouchMove={true}
          resistance={true}
          resistanceRatio={SWIPER_RESISTANCE_RATIO}
          effect="fade"
          fadeEffect={{
            crossFade: true
          }}
          className="w-full h-full"
        >
          {displayPhotos.map((filename, index) => (
            <SwiperSlide key={`${filename}-${index}`}>
              <div className="swiper-zoom-container w-full h-full flex items-center justify-center">
                {renderPhotoContent(index)}
              </div>
            </SwiperSlide>
          ))}

          {/* Navigation buttons */}
          <div className={`${SWIPER_BUTTON_CLASSES} slideshow-button-prev ${
            controlsVisible ? '!opacity-50 hover:!opacity-80' : '!opacity-0'
          }`}></div>
          <div className={`${SWIPER_BUTTON_CLASSES} slideshow-button-next ${
            controlsVisible ? '!opacity-50 hover:!opacity-80' : '!opacity-0'
          }`}></div>
        </Swiper>

        {/* Slideshow Controls */}
        <div
          ref={controlsRef}
          className={`${CONTROLS_CONTAINER_CLASSES} ${
            controlsVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
          onFocusCapture={showControls}
          onBlurCapture={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget)) showControls();
          }}
          data-testid="slideshow-controls"
        >
          <div className="flex items-center space-x-1 sm:space-x-3 bg-black/70 backdrop-blur-md rounded-full px-3 sm:px-6 py-2 sm:py-3 border border-accent-20 shadow-lg max-w-[95vw] overflow-x-auto">
            {/* Previous button */}
            <button
              onClick={goToPrev}
              className={CONTROL_BUTTON_CLASSES}
              title="Previous photo"
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-surface" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            {/* Play/Pause button */}
            <button
              onClick={togglePlayPause}
              className={CONTROL_BUTTON_CLASSES}
              title={isPlaying ? "Pause slideshow" : "Play slideshow"}
            >
              {isPlaying ? (
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-surface" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10 9v6m4-6v6" />
                </svg>
              ) : (
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-surface" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z" />
                </svg>
              )}
            </button>

            {/* Next button */}
            <button
              onClick={goToNext}
              className={CONTROL_BUTTON_CLASSES}
              title="Next photo"
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-surface" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>

            {/* Autoplay timing selector */}
            <div className="w-px h-4 sm:h-6 bg-surface-30 mx-0.5 sm:mx-1"></div>
            <select
              value={autoplayDelay}
              onChange={(e) => changeAutoplayTiming(parseInt(e.target.value))}
              className="bg-transparent text-surface text-xs border border-surface-30 rounded px-1.5 sm:px-2 py-0.5 sm:py-1 focus:outline-none focus:ring-1 focus:ring-surface-50 min-w-0"
              title="Slide duration"
            >
              {AUTOPLAY_OPTIONS.map(option => (
                <option key={option.value} value={option.value} className="bg-black">
                  {option.label}
                </option>
              ))}
            </select>

            {/* Skip videos toggle */}
            <div className="w-px h-4 sm:h-6 bg-surface-30 mx-0.5 sm:mx-1"></div>
            <button
              onClick={handleSkipVideosToggle}
              disabled={!hasPhotos}
              className={`p-1.5 sm:p-2 rounded-full transition-colors ${
                videosHidden
                  ? 'bg-primary text-surface hover:bg-primary-90' 
                  : 'bg-primary-20 text-surface hover:bg-primary-40'
              } ${!hasPhotos ? 'cursor-not-allowed opacity-60' : ''}`}
              title={!hasPhotos
                ? "Videos are shown because this gallery has no photos"
                : videosHidden
                  ? "Videos are hidden - Click to show videos"
                  : "Videos are shown - Click to hide videos"}
              aria-label={!hasPhotos
                ? "Videos are shown because this gallery has no photos"
                : videosHidden
                  ? "Videos are hidden - Show videos"
                  : "Videos are shown - Hide videos"}
            >
              <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                {videosHidden && (
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                )}
              </svg>
            </button>

            {/* Shuffle toggle */}
            <button
              onClick={handleShuffleToggle}
              className={`p-1.5 sm:p-2 rounded-full transition-colors ${
                shuffleMode 
                  ? 'bg-primary text-surface hover:bg-primary-90' 
                  : 'bg-primary-20 text-surface hover:bg-primary-40'
              }`}
              title={shuffleMode ? "Shuffle is ON - Click to turn off" : "Shuffle is OFF - Click to shuffle randomly"}
            >
              <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 8.5L21 6.5L19 4.5M21 6.5H18C16.5 6.5 15 7.5 14 9L10 15C9 16.5 7.5 17.5 6 17.5H3M19 15.5L21 17.5L19 19.5M21 17.5H18C16.5 17.5 15 16.5 14 15M3 6.5H6C7.5 6.5 9 7.5 10 9" />
              </svg>
            </button>

            {/* Repeat toggle */}
            <button
              onClick={handleRepeatToggle}
              className={`p-1.5 sm:p-2 rounded-full transition-colors ${
                repeatMode 
                  ? 'bg-primary text-surface hover:bg-primary-90' 
                  : 'bg-primary-20 text-surface hover:bg-primary-40'
              }`}
              title={repeatMode ? "Repeat is ON - Slideshow will loop" : "Repeat is OFF - Slideshow will stop at end"}
            >
              <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>

            {/* Fullscreen toggle - only show if supported */}
            {isFullscreenSupported && (
              <>
                <div className="w-px h-4 sm:h-6 bg-surface-30 mx-0.5 sm:mx-1"></div>
                <button
                  onClick={toggleFullscreen}
                  className={`p-1.5 sm:p-2 rounded-full transition-colors ${
                    isFullscreen 
                      ? 'bg-primary text-surface hover:bg-primary-90' 
                      : 'bg-primary-20 text-surface hover:bg-primary-40'
                  }`}
                  title={isFullscreen ? "Fullscreen is ON - Click to exit" : "Fullscreen is OFF - Click to enter"}
                >
                  {isFullscreen ? (
                    <MdFullscreenExit className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  ) : (
                    <MdFullscreen className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  )}
                </button>
              </>
            )}

            {/* Close button */}
            <div className="w-px h-4 sm:h-6 bg-surface-30 mx-0.5 sm:mx-1"></div>
            <button
              ref={closeButtonRef}
              onClick={handleClose}
              className="p-2 rounded-full hover:bg-surface-20 transition-colors text-red-400 hover:text-red-300"
              title="Close slideshow"
              aria-label="Close slideshow"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SlideshowModal;
